import { Link, useParams } from 'react-router-dom';
import { Route, Switch, useHistory } from 'react-router';
import { Alert, Drawer } from 'antd';
import { BackButtonLayout } from '../../../containers/BackButtonLayout';
import {
  FisheryProfileSection as Section,
  FisheryProfileSubSection as SubSection,
} from './FisheryProfileSection';
import MarkdownContent from '../../common/MarkdownContent';
import { ImageSlider } from '../../common/components/sliders';
import { SpeciesSummary } from './FeatureSummary';
import React, { useState } from 'react';

import '../assets/css/fishery_profile.css';
import { PlaceholderImage } from '../../common/components/sliders/components/SliderImages';
import ImagesLightboxModal from '../../common/ImagesLightboxModal';
import InlineChevronIcon from '../../../assets/images/InlineChevronIcon';
import {
  exotic_fishery,
  manor_fishery,
  manor_fishery_ids,
} from '../../common/Utils';
import { usePreventScroll } from '../../../utils/hooks';

function LakeQuickFacts({ guests, maxRods, access, anglingType, bookingType }) {
  const rowClasses =
    'display-flex-row justify-between fs-17 gRegular mg-bottom-15';

  const facts = [
    {
      key: 'guests',
      title: 'Non-Angling Guests',
      value: guests ? 'Yes' : 'No',
    },
    {
      key: 'maxRods',
      title: (
        <>
          Maximum Rods <span className={'gRegular'}>(per angler)</span>
        </>
      ),
      value: maxRods,
    },
    {
      key: 'access',
      title: 'Lake Access',
      value: access,
    },
    {
      key: 'anglingType',
      title: 'Angling Type',
      value: anglingType,
    },
    {
      key: 'bookingType',
      title: 'Booking Type',
      value: bookingType,
    },
  ];

  return (
    <div>
      {facts.map(({ key, title, value }) => (
        <div className={rowClasses} key={key}>
          <span className={'gSemiBold'}>{title}:</span>
          <span className={'cap'}>{value}</span>
        </div>
      ))}
    </div>
  );
}

export default function LakeDetails({ fisheryPublicId, lakes }) {
  const lakePublicId = useParams()['lake_id'];
  const drawerContentsKey = useParams()['drawer_key'];
  const [mapOpen, setMapOpen] = useState(false);

  const history = useHistory();

  const isDrawerOpen = Boolean(drawerContentsKey);

  // Prevent background app scroll when drawer is open
  usePreventScroll('LakeDetails', isDrawerOpen);

  const lakeData = lakes.find(
    (lake) => lake?.public_id.toString() === lakePublicId
  );
  if (!lakeData) {
    return (
      <Alert type={'error'} showIcon message={'Unable to get lake details'} />
    );
  }

  const readMoreDrawer = (
    <Drawer
      title={null}
      footer={null}
      closeable={true}
      maskCloseable={true}
      className={'fishery_profile__drawer'}
      open={isDrawerOpen}
      onClose={() => history.replace(`/fishery/${fisheryPublicId}`)}
    >
      <BackButtonLayout
        onBack={() =>
          history.replace(`/fishery/${fisheryPublicId}/lake/${lakePublicId}`)
        }
      >
        <div className={'main_container_padding'}>
          <div className={'main_container_padding__content'}>
            <Switch>
              <Route
                path={`/fishery/${fisheryPublicId}/lake/${lakePublicId}/rules`}
              >
                <Section title={lakeData.name + ' Rules'} noDivider>
                  <MarkdownContent
                    className={'fs-16 gRegular'}
                    content={lakeData.rules || 'No rules available yet.'}
                  />
                </Section>
              </Route>
            </Switch>
          </div>
        </div>
      </BackButtonLayout>
    </Drawer>
  );

  return (
    <>
      {readMoreDrawer}
      <div className={'lake_details__top_image_gallery mg-bottom-30'}>
        <ImageSlider
          height={'20rem'}
          images={(lakeData?.images?.secondary?.length
            ? lakeData.images.secondary
            : [lakeData?.images?.primary]
          )
            .filter((src) => src)
            .map((src) => ({ src, alt: lakeData.name }))}
          loading={!lakeData}
          openOnClick
          showButtons
        />
      </div>
      <Section title={lakeData.name} noDivider>
        {fisheryPublicId !== exotic_fishery ? (
          <p className={'mg-bottom-8'}>
            <Link
              to={`/fishery/${fisheryPublicId}/lake/${lakePublicId}/rules`}
              className={
                'fs-16 gRegular black txt-underline black txt-underline'
              }
            >
              <span>
                {lakeData?.accommodation
                  ? 'Check Booking Rules'
                  : 'Check lake rules'}
              </span>
              <InlineChevronIcon />
            </Link>
          </p>
        ) : (
          ''
        )}

        <SubSection title={'Description'} noDivider={false}>
          <MarkdownContent
            className={'fs-16 gRegular'}
            content={lakeData.description || 'No description available yet.'}
          />
        </SubSection>
        {!lakeData?.accommodation && (
          <>
            <SubSection title={'Lake Map'}>
              <PlaceholderImage
                className={'drawer_image--full-width'}
                src={lakeData?.images?.primary}
                alt={'Lake map for ' + lakeData.name}
                onClick={() => setMapOpen(true)}
              />
              <ImagesLightboxModal
                isOpen={mapOpen}
                onClose={() => setMapOpen(false)}
                images={[
                  {
                    src: lakeData?.images?.primary,
                    alt: 'Lake map for ' + lakeData.name,
                  },
                ]}
                loading={!lakeData?.images?.primary}
                hideImageCount
              />
            </SubSection>

            <SubSection title={'Species'} noDivider={false}>
              <SpeciesSummary
                species={lakeData.species}
                wide
                publicId={fisheryPublicId}
              />
            </SubSection>
          </>
        )}

        {lakeData?.booking_info && (
          <SubSection
            title={
              fisheryPublicId == exotic_fishery || lakeData?.accommodation
                ? 'Booking Information'
                : 'Lake Booking Information'
            }
            noDivider={false}
          >
            <MarkdownContent
              className={'fs-16 gRegular'}
              content={lakeData?.booking_info}
            />
          </SubSection>
        )}
        {fisheryPublicId !== exotic_fishery && !lakeData?.accommodation && (
          <SubSection title={'Facts'}>
            <div className={'mg-bottom-20'}>
              <LakeQuickFacts
                guests={Boolean(lakeData?.allow_non_angling_guest === 'yes')}
                maxRods={lakeData?.max_rod}
                access={lakeData?.access_type}
                anglingType={lakeData?.type}
                bookingType={
                  lakeData?.booking_type +
                  (!lakeData?.lake_exclusive_bookings
                    ? ' Only'
                    : ' or Lake Exclusive')
                }
              />
            </div>
          </SubSection>
        )}
      </Section>
    </>
  );
}
